import React from 'react';
import { motion } from 'framer-motion';
import { Download, Upload, Check, Star, Home, Briefcase } from 'lucide-react';
import useComparisonStore from '../store/comparisonStore';
import { getPackageCardClasses } from '../utils/formatters';
import { cardHoverVariants } from '../utils/animations';

/**
 * PackageCard component
 * Displays information about a single package
 */
const PackageCard = ({
  pkg,
  index,
  onSelect = () => {},
  isSelected = false,
  isDisabled = false,
  disabledReason = ''
}) => {
  const { formatSpeed, getUsageProfile } = useComparisonStore();
  
  // Check if we have the original package data or use the direct properties
  const originalPkg = pkg.originalPackage || pkg;
  const downloadSpeed = originalPkg["Download speed (kbps)"] ? originalPkg["Download speed (kbps)"] / 1024 : 0;
  const uploadSpeed = originalPkg["Upload speed (kbps)"] ? originalPkg["Upload speed (kbps)"] / 1024 : 0;
  const usageProfile = getUsageProfile(pkg);
  const isSymmetrical = downloadSpeed === uploadSpeed;
  const isBusinessPackage = pkg.connectionType === 'FTTB';
  
  // Get CSS classes for styling
  const classes = getPackageCardClasses(pkg);
  
  return (
    <motion.div
      className={classes.card}
      variants={cardHoverVariants}
      initial="initial"
      whileHover="hover"
      layout
    >
      {/* Package Type Badge */}
      <div className={classes.badge}>
        {isBusinessPackage ? 'BUSINESS' : 'HOME'}
      </div>
      
      {/* Package Header */}
      <div className="p-4 border-b border-gray-700 flex justify-between items-start">
        <div>
          <div className="flex items-center">
            {pkg.operator?.logo ? (
              <img 
                src={pkg.operator.logo} 
                alt={pkg.operatorName} 
                className="h-5 w-5 object-contain mr-2"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className={classes.icon}>
                {isBusinessPackage
                  ? <Briefcase className="h-3 w-3 text-blue-400" />
                  : <Home className="h-3 w-3 text-purple-400" />
                }
              </div>
            )}
            <h3 className="font-medium text-white text-sm">{pkg.operatorName}</h3>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {isBusinessPackage ? 'Business Fibre (FTTB)' : 'Home Fibre (FTTH)'}
          </div>
        </div>
        
        {usageProfile && (
          <div className={classes.profileBadge}>
            <Star className="h-3 w-3 mr-1" />
            {usageProfile}
          </div>
        )}
      </div>
      
      {/* Package Details */}
      <div className="p-4">
        {/* Speed */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-2xl font-bold text-white">{formatSpeed(originalPkg["Download speed (kbps)"])}</div>
            <div className="flex items-center text-xs text-gray-400">
              <Download className="h-3 w-3 mr-1" />
              <span>{downloadSpeed} Mbps</span>
              <Upload className="h-3 w-3 ml-2 mr-1" />
              <span>{uploadSpeed} Mbps</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-white">R{pkg.Price}</div>
            <div className="text-xs text-gray-400">per month</div>
          </div>
        </div>
        
        {/* Connection Type */}
        <div className="mb-4">
          <div className={classes.connectionType}>
            <div className="text-sm font-medium flex items-center">
              {isBusinessPackage
                ? <Briefcase className="h-4 w-4 mr-2" />
                : <Home className="h-4 w-4 mr-2" />
              }
              {isBusinessPackage ? 'Business Fibre' : 'Home Fibre'}
              {isSymmetrical && <span className="ml-1">(Symmetrical)</span>}
            </div>
          </div>
          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
            <div
              className={classes.progressBar}
              style={{ width: isSymmetrical ? '100%' : '50%' }}
            ></div>
          </div>
        </div>
        
        {/* Features */}
        <div className="space-y-1 mb-4">
          <div className="flex items-center text-xs text-gray-300">
            <Check className="h-3 w-3 text-green-400 mr-2" />
            <span>No throttling or shaping</span>
          </div>
          <div className="flex items-center text-xs text-gray-300">
            <Check className="h-3 w-3 text-green-400 mr-2" />
            <span>Free installation & router</span>
          </div>
          <div className="flex items-center text-xs text-gray-300">
            <Check className="h-3 w-3 text-green-400 mr-2" />
            <span>Month-to-month contract</span>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col space-y-2">
          {/* Check Availability Button */}
          <motion.button
            className={`${classes.button} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => {
              if (!isDisabled) {
                onSelect(pkg, 'availability');
              }
            }}
            whileHover={!isDisabled ? { scale: 1.02 } : {}}
            whileTap={!isDisabled ? { scale: 0.98 } : {}}
            disabled={isDisabled}
          >
            {isDisabled ? disabledReason : 'Check Availability'}
          </motion.button>
          
          {/* Compare Button */}
          <motion.button
            className={`${isSelected
              ? 'bg-purple-600 text-white'
              : 'bg-gray-700 text-white hover:bg-gray-600'}
              py-2 rounded-lg text-sm font-medium transition-colors
              ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => {
              if (!isDisabled) {
                onSelect(pkg, 'compare');
              }
            }}
            whileHover={!isDisabled ? { scale: 1.02 } : {}}
            whileTap={!isDisabled ? { scale: 0.98 } : {}}
            disabled={isDisabled}
          >
            {isDisabled
              ? disabledReason
              : isSelected
                ? 'Added to Comparison'
                : 'Compare'}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default PackageCard;